'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Post } from '@/lib/content-source';
import ArticleCard from './ArticleCard';
import Pagination from './Pagination';
import { useAuth } from '@/lib/auth';
import { SignUpPrompt } from './UpgradePrompt';

interface CategoryPageProps {
  category: string;
  title: string;
  description: string;
  posts: Post[];
  icon?: string;
  postsPerPage?: number;
  actionButton?: {
    label: string;
    href: string;
    icon?: string;
  };
  hideBreadcrumbs?: boolean;
}

export default function CategoryPage({
  category,
  title,
  description,
  posts,
  icon = '📚',
  postsPerPage = 12,
  actionButton,
  hideBreadcrumbs = false
}: CategoryPageProps) {
  // Validate postsPerPage to prevent division by zero
  const validPostsPerPage = Math.max(1, postsPerPage);

  const { isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'title'>('date');

  // Handle client-side mounting and URL sync
   
  useEffect(() => {
    setMounted(true);

    // Sync with URL on mount
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('page') || '1', 10);
      const validPage = Math.max(1, page);
      setCurrentPage(validPage);
    }
  }, []);
   

  // Reset to page 1 when posts or category changes
   
  useEffect(() => {
    if (!mounted) return;

    setCurrentPage(1);
    // Update URL to page 1 without adding to history
    if (typeof window !== 'undefined') {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('page');
      window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
    }
  }, [posts, category, mounted]);
   

  // Get all unique tags from posts
  const allTags = Array.from(new Set(posts.flatMap(p => p.tags))).sort();

  // Filter and sort posts
  const filteredPosts = posts
    .filter(p => {
      // Search filter
      const matchesSearch = !searchQuery ||
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      // Tag filter
      const matchesTag = !selectedTag || p.tags.includes(selectedTag);

      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      } else {
        return a.title.localeCompare(b.title);
      }
    });

  // Calculate pagination data
  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);
  const totalPages = Math.ceil(regularPosts.length / validPostsPerPage);
  const startIndex = (currentPage - 1) * validPostsPerPage;
  const endIndex = startIndex + validPostsPerPage;
  const currentPosts = regularPosts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Update URL with page number using history API
    if (typeof window !== 'undefined') {
      const newUrl = new URL(window.location.href);
      if (page === 1) {
        newUrl.searchParams.delete('page');
      } else {
        newUrl.searchParams.set('page', page.toString());
      }
      window.history.pushState({}, '', newUrl.pathname + newUrl.search);
    }
    
    // Scroll to content section instead of top of page
    const contentElement = document.getElementById('content-section');
    if (contentElement) {
      contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback: scroll to a reasonable position keeping header visible
      const headerHeight = 200;
      window.scrollTo({ top: headerHeight, behavior: 'smooth' });
    }
  };
  return (
    <div className="container mx-auto px-4 py-12">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-4xl">{icon}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground">
            {title}
          </h1>
        </div>
        <div className="flex items-start justify-between gap-4">
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl">
            {description}
          </p>
          {actionButton && (
            <Link
              href={actionButton.href}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors whitespace-nowrap"
            >
              {actionButton.icon && <span>{actionButton.icon}</span>}
              {actionButton.label}
            </Link>
          )}
        </div>
      </div>

      {/* Breadcrumb */}
      {!hideBreadcrumbs && (
        <nav aria-label="Breadcrumb" className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <ol className="flex items-center">
            <li>
              <Link href="/" className="hover:text-accent transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="mx-2">/</li>
            <li aria-current="page" className="text-light-text dark:text-foreground">
              {title}
            </li>
          </ol>
        </nav>
      )}

      {/* Search and Filter Controls */}
      {posts.length > 0 && (
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to page 1 on search
              }}
              className="w-full px-4 py-3 pl-12 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder-light-text-secondary dark:placeholder-foreground-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/20"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light-text-secondary dark:text-foreground-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-light-text-secondary dark:text-foreground-tertiary hover:text-accent"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-4 items-center">
            {/* Tag Filter */}
            {allTags.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <select
                  value={selectedTag || ''}
                  onChange={(e) => {
                    setSelectedTag(e.target.value || null);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/20"
                >
                  <option value="">All Tags</option>
                  {allTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Sort Control */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'title')}
                className="px-3 py-2 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/20"
              >
                <option value="date">Newest First</option>
                <option value="title">Title (A-Z)</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || selectedTag) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedTag(null);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 text-sm bg-accent-muted text-accent hover:bg-accent-muted rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {(searchQuery || selectedTag) && (
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className="text-light-text-secondary dark:text-foreground-tertiary">Active filters:</span>
              {searchQuery && (
                <span className="px-3 py-1 bg-accent-muted text-accent rounded-full flex items-center gap-2">
                  Search: &quot;{searchQuery}&quot;
                  <button
                    onClick={() => setSearchQuery('')}
                    className="hover:text-light-text dark:hover:text-foreground"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedTag && (
                <span className="px-3 py-1 bg-accent-muted text-accent rounded-full flex items-center gap-2">
                  Tag: {selectedTag}
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="hover:text-light-text dark:hover:text-foreground"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sign-up Prompt for logged-out users */}
      {mounted && !isAuthenticated && posts.length > 0 && (
        <div className="mb-8">
          <SignUpPrompt
            feature="saving content and tracking your learning progress"
            variant="banner"
          />
        </div>
      )}

      {/* Content */}
      {posts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-light-text-secondary dark:text-foreground-tertiary text-lg mb-4">
            No {category} available yet.
          </p>
          <p className="text-light-text-tertiary dark:text-foreground-muted">
            Check back soon for new content!
          </p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-light-text-secondary dark:text-foreground-tertiary text-lg mb-4">
            No results found for your search.
          </p>
          <p className="text-light-text-tertiary dark:text-foreground-muted mb-6">
            Try adjusting your filters or search query.
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedTag(null);
            }}
            className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      ) : (
        <div id="content-section">
          {/* Stats */}
          <div className="mb-8 flex gap-6 text-sm text-light-text-secondary dark:text-foreground-tertiary">
            <span>
              {filteredPosts.length} {filteredPosts.length === 1 ? 'item' : 'items'}
              {(searchQuery || selectedTag) && ` (filtered from ${posts.length})`}
            </span>
            {featuredPosts.length > 0 && (
              <span>{featuredPosts.length} featured</span>
            )}
          </div>

          {/* Featured Posts - Always shown on page 1 */}
          {featuredPosts.length > 0 && currentPage === 1 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Featured</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {featuredPosts.map((post) => (
                  <ArticleCard
                    key={post.slug}
                    post={post}
                    category={post.category}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Posts - Paginated */}
          <div>
            {featuredPosts.length > 0 && (
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">All {title}</h2>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentPosts.map((post) => (
                <ArticleCard
                  key={post.slug}
                  post={post}
                  category={post.category}
                />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="mt-16 p-8 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border text-center">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
          Want to contribute?
        </h2>
        <p className="text-light-text-secondary dark:text-foreground-secondary mb-6 max-w-2xl mx-auto">
          Have experience to share? We welcome contributions from technical leaders.
        </p>
        <Link
          href="/about"
          className="inline-block px-6 py-3 bg-accent text-accent-foreground font-bold rounded-lg hover:bg-accent-hover transition-colors"
        >
          Learn More
        </Link>
      </div>
    </div>
  );
}
